// OWChart.jsx
import React, { useMemo, useRef, useCallback, useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import 'chartjs-adapter-luxon';
import { DateTime } from 'luxon';
import './OWChart.css';
import useChartDragZoom from '../useChartDragZoom';
import useIsNarrowScreen from '../useIsNarrowScreen';
import { downloadChartImage, getAutoScaleFromData } from '../downloadChartImage';
import {
    pickSpanModeForScreen,
    createTickLabelFormatter,
    createFixedGridTicksPlugin,
    createTimeScaleConfig,
    niceStep,
} from '../timeScaleUtils';
import { crosshairYPlugin } from '../../../plugins';

// ─────────────────────────────────────────────────────────────────────────────

const OWChart = ({ measurements = [], deviceName, slopeData, deviceId }) => {
    const { points, thresholds, minY, spanMs, deep } = useMemo(() => {
        const sorted = (measurements || [])
            .slice()
            .sort((a, b) => new Date(a.obs_time) - new Date(b.obs_time));

        const pts = sorted
            .map((item) => {
                const dt = DateTime.fromISO(item.obs_time).startOf('minute');
                if (!dt.isValid) return null;
                return { x: dt.toMillis(), y: Number(item.value) || 0 };
            })
            .filter(Boolean);

        const ys = pts.map((p) => p.y);
        let minY = 0;
        if (ys.length) {
            minY = ys[0];
            for (const value of ys) {
                if (value < minY) minY = value;
            }
        }

        const deviceData = slopeData?.water_level?.find((d) => d.id === deviceId);
        const deep = deviceData?.deep;
        const mkConstSeries = (yConst) => pts.map((p) => ({ x: p.x, y: Number(yConst) }));
        const thr = [];
        var warning_up = deviceData?.warning_up;
        var action_up = deviceData?.action_up;
        var water_level_head = deviceData?.water_level_head;
        if (water_level_head) {
            warning_up = water_level_head - warning_up;
            action_up = water_level_head - action_up;
        }

        if (deviceData?.warning_up != null)
            thr.push({
                label: '警戒上限',
                color: '#FFA500',
                dash: [5, 5],
                data: mkConstSeries(warning_up * -1),
            });
        if (deviceData?.warning_down != null)
            thr.push({
                label: '警戒下限',
                color: '#FFA500',
                dash: [5, 5],
                data: mkConstSeries(deviceData.warning_down * -1),
            });
        if (deviceData?.action_up != null)
            thr.push({
                label: '行動上限',
                color: '#FF0000',
                dash: [10, 5],
                data: mkConstSeries(action_up * -1),
            });
        if (deviceData?.action_down != null)
            thr.push({
                label: '行動下限',
                color: '#FF0000',
                dash: [10, 5],
                data: mkConstSeries(deviceData.action_down * -1),
            });

        let spanMs = 0;
        if (pts.length >= 2) {
            const xs = pts.map((p) => p.x).sort((a, b) => a - b);
            spanMs = xs[xs.length - 1] - xs[0];
        }

        return { points: pts, thresholds: thr, minY, spanMs, deep };
    }, [measurements, slopeData, deviceId]);

    const chartRef = useRef(null);
    const containerRef = useRef(null);
    const [chartWidth, setChartWidth] = useState(800);

    // 監聽容器寬度變化
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const updateWidth = () => {
            setChartWidth(container.clientWidth || 800);
        };

        updateWidth();
        const resizeObserver = new ResizeObserver(updateWidth);
        resizeObserver.observe(container);

        return () => resizeObserver.disconnect();
    }, []);

    const isNarrowScreen = useIsNarrowScreen();
    const { zoomRange, isZoomed, resetZoom, selectionOverlayPlugin } = useChartDragZoom(
        chartRef,
        { enabled: !isNarrowScreen }
    );
    const effectiveSpanMs = useMemo(() => {
        const span = zoomRange
            ? Math.max(zoomRange.max - zoomRange.min, 0)
            : spanMs;
        if (!Number.isFinite(span) || span <= 0) return spanMs;
        return span;
    }, [zoomRange, spanMs]);
    // 根據螢幕寬度選擇適合的刻度配置
    const activeSpanMode = pickSpanModeForScreen(effectiveSpanMs, chartWidth);
    const safeMaxTicks = useMemo(() => {
        const maxTicks = chartWidth ? Math.floor(chartWidth / 50) : 12;
        return Math.max(4, Math.min(maxTicks, 20));
    }, [chartWidth]);
    const displayMaxTicks = useMemo(() => {
        if (!isNarrowScreen) return safeMaxTicks;
        return Math.min(safeMaxTicks, 6);
    }, [isNarrowScreen, safeMaxTicks]);
    const estimatedTicks = useMemo(() => {
        if (!Number.isFinite(effectiveSpanMs) || effectiveSpanMs <= 0) return 0;
        if (activeSpanMode.stepHours) {
            return effectiveSpanMs / (activeSpanMode.stepHours * 60 * 60 * 1000);
        }
        if (activeSpanMode.stepDays) {
            return effectiveSpanMs / (activeSpanMode.stepDays * 24 * 60 * 60 * 1000);
        }
        if (activeSpanMode.stepMonths) {
            return effectiveSpanMs / (activeSpanMode.stepMonths * 30 * 24 * 60 * 60 * 1000);
        }
        return 0;
    }, [effectiveSpanMs, activeSpanMode]);
    const shouldAutoSkip = estimatedTicks > safeMaxTicks;
    const tickLabelFormatter = useMemo(
        () => createTickLabelFormatter(activeSpanMode.mode),
        [activeSpanMode.mode]
    );
    const sparseTickFormatter = useMemo(() => {
        return (value, index, ticks) => {
            const total = ticks?.length || 0;
            const step = total > 0 ? Math.ceil(total / displayMaxTicks) : 1;
            if (step > 1 && index % step !== 0) return '';
            return tickLabelFormatter(value, index, ticks);
        };
    }, [displayMaxTicks, tickLabelFormatter]);
    const chartTitleText = [deviceName, '水位觀測井歷史趨勢', `(深度 ${deep} 公尺)`]
        .filter(Boolean)
        .join(' ');
    const xMin = useMemo(() => {
        if (zoomRange?.min != null) return zoomRange.min;
        const first = points?.[0]?.x;
        if (first == null) return undefined;
        const dt = DateTime.fromMillis(first).setZone('Asia/Taipei');
        if (activeSpanMode.unit === 'day') return dt.startOf('day').toMillis();
        if (activeSpanMode.unit === 'hour') return dt.startOf('hour').toMillis();
        if (activeSpanMode.unit === 'minute') return dt.startOf('minute').toMillis();
        return first;
    }, [zoomRange?.min, points, activeSpanMode.unit]);
    const xMax = zoomRange?.max;

    // x 軸 time 設定 - 使用統一的時間刻度配置（必須在條件返回之前）
    const xTime = useMemo(() => {
        const baseConfig = createTimeScaleConfig(activeSpanMode);
        return {
            ...baseConfig,
            bounds: 'data',
            displayFormats: {
                millisecond: 'HH:mm:ss',
                second: 'HH:mm:ss',
                minute: 'HH:mm',
                hour: 'MM-dd HH:mm',
                day: 'M/d',
                month: 'yyyy-MM',
                year: 'yyyy',
            },
            tooltipFormat: 'yyyy/MM/dd HH:mm:ss',
        };
    }, [activeSpanMode]);

    const handleDownloadChart = useCallback(() => {
        const chart = chartRef.current;
        if (!chart) return;

        // 自動根據數據時間範圍選擇適合的 scale
        const autoScale = getAutoScaleFromData(points);

        const timestamp = DateTime.local().setZone('Asia/Taipei').toFormat('yyyy_MM');

        downloadChartImage(chart, {
            fileNameBase: deviceName || 'water-level',
            fileNameSuffix: timestamp,
            scale: autoScale,
            width: 800,
            height: 600,
        });
    }, [deviceName, points]);

    const dataRangeKey = useMemo(() => {
        const first = points[0]?.x;
        const last = points[points.length - 1]?.x;
        return `${first ?? 'na'}-${last ?? 'na'}-${points.length}`;
    }, [points]);

    useEffect(() => {
        resetZoom();
    }, [deviceId, dataRangeKey, resetZoom]);

    // Y 軸：上 0、下取最負值
    // 深度 <= 25 時 step 為 2，> 25 時 step 為 5
    const minNegative = deep ? -Math.abs(deep) : Math.min(0, minY);
    const absDeep = deep ? Math.abs(deep) : Math.abs(minNegative);
    const stepY = absDeep <= 25 ? 2 : 5;
    const yMin = deep ? minNegative : minNegative === 0 ? -stepY : minNegative;
    const yMax = 0;
    const tickAbsMax = Math.floor(Math.abs(yMin) / stepY) * stepY;
    const yTickValues = useMemo(() => {
        const ticks = [];
        for (let v = 0; v <= tickAbsMax + 1e-9; v += stepY) {
            ticks.push(-v);
        }
        return ticks;
    }, [tickAbsMax, stepY]);

    if (!points || points.length === 0) {
        return (
            <div className="chart-no-data">
                <p>暫無測量資料</p>
            </div>
        );
    }

    const chartData = {
        datasets: [
            {
                label: `${deviceName ?? ''} 水位高度`,
                data: points,
                borderColor: '#3B82F6',
                backgroundColor: 'rgba(221, 93, 81, 0.1)',
                tension: 0.1,
                pointRadius: 3,
                pointHoverRadius: 6,
                pointBackgroundColor: '#3B82F6',
                pointBorderColor: '#3B82F6',
                pointBorderWidth: 0,
                pointHoverBackgroundColor: '#3B82F6',
                yAxisID: 'y',
                parsing: false,
            },
            ...thresholds.map((t) => ({
                label: t.label,
                data: t.data,
                borderColor: t.color,
                borderWidth: 1.5,
                backgroundColor: 'transparent',
                // borderDash: t.dash,
                pointRadius: 0,
                pointHoverRadius: 0,
                yAxisID: 'y',
                parsing: false,
            })),
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            crosshairY: {
                enabled: true,
                lineColor: 'rgba(0, 0, 0, 0.5)',
                lineWidth: 1,
                lineDash: [5, 5],
                showVerticalLine: true,
                showHorizontalLine: true,
                decimals: 0,
                unit: 'm',
            },
            title: {
                display: true,
                text: chartTitleText || '水位觀測井歷史趨勢',
                color: '#1f2937',
                font: { size: 16, weight: '600' },
                padding: { top: 10, bottom: 20 },
            },
            legend: { display: true, position: 'top' },
            tooltip: {
                callbacks: {
                    title: (items) =>
                        DateTime.fromMillis(items[0].parsed.x).toFormat(
                            'yyyy/MM/dd HH:mm:ss'
                        ),
                    label: (ctx) => {
                        const val = ctx.parsed.y;
                        if (
                            ctx.dataset.label.includes('警戒') ||
                            ctx.dataset.label.includes('行動')
                        ) {
                            return `${ctx.dataset.label}: ${val.toFixed(2)} m`;
                        }
                        return `水位高度: ${val.toFixed(2)} m`;
                    },
                },
            },
            decimation: { enabled: true, algorithm: 'lttb', samples: 1000 },
        },
        scales: {
            x: {
                type: 'time',
                time: xTime, // 仍含 zone: 'Asia/Taipei'
                bounds: 'data',
                min: xMin,
                max: xMax,
                ticks: {
                    source: 'ticks',
                    autoSkip: shouldAutoSkip,
                    maxTicksLimit: displayMaxTicks,
                    autoSkipPadding: isNarrowScreen ? 6 : 12,
                    includeBounds: true,
                    maxRotation: 0,
                    minRotation: 0,
                    major: { enabled: true }, // 可選：讓 00:00 粗體
                    font: { size: isNarrowScreen ? 11 : 14, lineHeight: 1.2 },
                    padding: isNarrowScreen ? 6 : 12,
                    // maxTicksLimit 可拿掉；若想更稀疏，直接改 stepHours
                    callback: sparseTickFormatter,
                },
                grid: { drawTicks: true },
                title: { display: true, text: '時間' },
            },
            y: {
                type: 'linear',
                min: yMin,
                max: yMax,
                afterBuildTicks: (scale) => {
                    scale.ticks = yTickValues.map((value) => ({ value }));
                },
                title: { display: true, text: '水位高度 (m)' },
                ticks: {
                    stepSize: stepY,
                    callback: (v) => (Number.isInteger(v) ? v : ''),
                    format: { maximumFractionDigits: 0 },
                },
                grid: { drawTicks: true },
            },
        },
    };

    // ★ 使用統一的固定網格刻度插件
    const dynamicPlugins = [
        selectionOverlayPlugin,
        createFixedGridTicksPlugin(activeSpanMode),
        crosshairYPlugin,
    ];

    return (
        <div className="ow-chart-container" ref={containerRef}>
            <div className="chart-header">
                <div className="chart-actions">
                    {isZoomed && (
                        <button
                            type="button"
                            className="chart-reset-button"
                            onClick={resetZoom}
                        >
                            重置縮放
                        </button>
                    )}
                    {!isNarrowScreen && (
                        <button
                            type="button"
                            className="chart-download-button"
                            onClick={handleDownloadChart}
                        >
                            下載圖表
                        </button>
                    )}
                </div>
            </div>
            <div className="chart-wrapper">
                <Line
                    key={deviceId ? `ow-${deviceId}-${dataRangeKey}` : 'ow'}
                    ref={chartRef}
                    data={chartData}
                    options={options}
                    plugins={dynamicPlugins}
                />
            </div>
        </div>
    );
};

export default OWChart;
